import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { useNavigate } from "react-router-dom"
import loginImage from "../../../app/assets/img/login-image.jpg"
import SvgLogo from "../../../app/assets/img/logo.svg"
import useAuthStore from "@features/auth/authStore.client"
import InstallPortalButton from "@features/pwa/InstallPortalButton"

const PageWrapper = styled.main`
  min-height: 100svh;
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(520px, 0.92fr);
  background: #f4f8fc;
  color: #16324f;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`

const ImagePanel = styled.section`
  position: relative;
  min-height: 100svh;
  display: flex;
  align-items: flex-end;
  padding: clamp(2rem, 5vw, 5rem);
  isolation: isolate;
  overflow: hidden;
  background: #0a2038 url(${loginImage}) center / cover no-repeat;
  color: #ffffff;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -2;
    background: linear-gradient(180deg, rgba(5, 21, 39, 0.12) 10%, rgba(7, 28, 50, 0.46) 55%, rgba(6, 25, 45, 0.92) 100%);
  }

  &::after {
    content: "";
    position: absolute;
    inset: auto -15% -20% 18%;
    height: 48%;
    z-index: -1;
    background: radial-gradient(circle, rgba(70, 157, 226, 0.36), transparent 68%);
    filter: blur(35px);
  }

  @media (max-width: 980px) {
    min-height: 310px;
    padding: 2rem;
    background-position: center 54%;
  }

  @media (max-width: 560px) {
    min-height: 250px;
    padding: 1.4rem;
  }
`

const ImageContent = styled.div`
  width: min(620px, 100%);
  display: grid;
  gap: 1rem;
`

const ImageEyebrow = styled.span`
  width: fit-content;
  padding: 0.45rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 2px;
  background: rgba(7, 29, 51, 0.52);
  backdrop-filter: blur(10px);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
`

const ImageTitle = styled.h2`
  max-width: 600px;
  color: #ffffff;
  font-size: clamp(2rem, 4vw, 3.6rem);
  line-height: 1.05;
  letter-spacing: -0.04em;
`

const ImageText = styled.p`
  max-width: 560px;
  margin: 0;
  color: rgba(255, 255, 255, 0.84);
  font-size: clamp(1rem, 1.4vw, 1.12rem);
  line-height: 1.65;

  @media (max-width: 560px) {
    display: none;
  }
`

const LoginPanel = styled.section`
  position: relative;
  min-height: 100svh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(5rem, 9vw, 7rem) clamp(2rem, 7vw, 6rem) 3rem;
  background: radial-gradient(circle at 100% 0%, rgba(79, 153, 214, 0.12), transparent 34%), linear-gradient(145deg, #ffffff 0%, #f4f8fc 100%);

  @media (max-width: 980px) {
    min-height: auto;
    padding: 5.5rem 1.5rem 3rem;
  }
`

const HomeLink = styled.a`
  position: absolute;
  top: 2rem;
  left: clamp(2rem, 7vw, 6rem);
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: #275b8b;
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: none;

  &::before {
    content: "←";
    font-size: 1.1rem;
    transition: transform 0.2s ease;
  }

  &:hover::before {
    transform: translateX(-3px);
  }

  @media (max-width: 980px) {
    left: 1.5rem;
    top: 1.6rem;
  }
`

const LoginContent = styled.div`
  width: min(470px, 100%);
`

const BrandLink = styled.a`
  display: inline-flex;
  width: 215px;
  margin-bottom: 2.5rem;

  svg {
    width: 100%;
    height: auto;
  }
`

const Eyebrow = styled.p`
  margin: 0 0 0.6rem;
  color: #337abd;
  font-size: 0.77rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`

const FormTitle = styled.h1`
  margin: 0;
  color: #102f4f;
  font-size: clamp(2.15rem, 4vw, 3rem);
  line-height: 1.05;
  letter-spacing: -0.04em;
`

const FormIntro = styled.p`
  margin: 1rem 0 2rem;
  color: #5c6f82;
  font-size: 1rem;
  line-height: 1.65;
`

const Form = styled.form`
  display: grid;
  gap: 1.2rem;
`

const Field = styled.label`
  display: grid;
  gap: 0.5rem;
`

const FieldLabel = styled.span`
  color: #16324f;
  font-size: 0.9rem;
  font-weight: 700;
`

const Input = styled.input`
  width: 100%;
  min-height: 56px;
  padding: 0 1rem;
  border: 1px solid #c8d6e4;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.9);
  color: #10283f;
  font-size: 1rem;
  box-shadow: 0 1px 2px rgba(17, 50, 82, 0.04);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  &::placeholder {
    color: #8a99a8;
  }

  &:hover {
    border-color: #9eb5ca;
  }

  &:focus {
    outline: none;
    border-color: #337abd;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(51, 122, 189, 0.13);
  }
`

const Button = styled.button`
  width: 100%;
  min-height: 58px;
  margin-top: 0.25rem;
  padding: 0 1.25rem;
  border: 0;
  border-radius: 2px;
  background: linear-gradient(135deg, #337abd 0%, #1d5f98 100%);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 14px 32px rgba(37, 95, 145, 0.24);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 18px 38px rgba(37, 95, 145, 0.32);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`

const ErrorBanner = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #efb9b9;
  border-radius: 2px;
  background: #fff2f2;
  color: #a52c2c;
  font-size: 0.9rem;
`

const SupportRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dbe4ec;
  color: #697b8c;
  font-size: 0.88rem;

  a {
    color: #275b8b;
    font-weight: 800;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const SecurityNote = styled.p`
  margin: 1.5rem 0 0;
  color: #7a8b9a;
  font-size: 0.8rem;
  line-height: 1.5;
`

const InstallArea = styled.div`
  margin-top: 1rem;

  button {
    width: 100%;
    min-height: 48px;
  }
`

const ClientLogin = () => {
  const navigate = useNavigate()
  const { login, loading, setLoading, error, clearError } = useAuthStore()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    clearError()
    try {
      await login(email, password)
      navigate("/app")
    } catch {
      // Le store d'authentification expose déjà le message d'erreur à l'écran.
    }
  }

  useEffect(() => {
    setLoading(false)
    clearError()
  }, [clearError, setLoading])

  return (
    <PageWrapper>
      <ImagePanel aria-label="Paysage de la région de Portneuf">
        <ImageContent>
          <ImageEyebrow>Espace TB</ImageEyebrow>
          <ImageTitle>Vos demandes, vos immeubles et vos suivis.</ImageTitle>
          <ImageText>Un accès simple aux outils et aux renseignements mis à votre disposition par notre équipe.</ImageText>
        </ImageContent>
      </ImagePanel>

      <LoginPanel>
        <HomeLink href="/">Retour au site</HomeLink>
        <LoginContent>
          <BrandLink href="/" aria-label="TB Gestion Immobilière — retour à l’accueil">
            <SvgLogo />
          </BrandLink>
          <Eyebrow>Espace sécurisé</Eyebrow>
          <FormTitle>Connexion à Espace TB</FormTitle>
          <FormIntro>Locataires, propriétaires et membres de l’équipe utilisent les accès transmis par TB Gestion Immobilière.</FormIntro>

          <Form onSubmit={handleSubmit}>
            <Field>
              <FieldLabel>Adresse courriel</FieldLabel>
              <Input
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                placeholder="nom@exemple.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Mot de passe</FieldLabel>
              <Input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="Votre mot de passe"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </Field>
            <Button type="submit" disabled={loading}>
              {loading ? "Connexion en cours…" : "Se connecter"}
            </Button>
          </Form>

          {error && (
            <ErrorBanner role="alert" aria-live="polite">
              {error}
            </ErrorBanner>
          )}

          <SupportRow>
            <a href="/mot-de-passe-oublie">Mot de passe oublié?</a>
            <a href="mailto:info@tbgestionimmobiliere.com">Contacter notre équipe</a>
          </SupportRow>
          <SecurityNote>Vos identifiants servent uniquement à accéder à votre espace sécurisé.</SecurityNote>
          <InstallArea>
            <InstallPortalButton label="Ajouter Espace TB à cet appareil" />
          </InstallArea>
        </LoginContent>
      </LoginPanel>
    </PageWrapper>
  )
}

export default ClientLogin
